// src/features/treinos/services/workoutLogService.js
import { db } from "../../../firebaseConfig.js";
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
  limit as fsLimit,
} from "firebase/firestore";
import { WORKOUT_SESSIONS_WINDOW } from "./exerciseStatsService.js";
import { epley1RM } from "../utils/epley.js";
import { exMatches, normNome } from "../utils/exerciseStats.js";

// ── Helper ────────────────────────────────────────────────────────────────────

function dataLocalSP() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

// ── Referência ────────────────────────────────────────────────────────────────

function sessaoRef(uid, sessaoId) {
  return doc(db, "PACIENTE", uid, "WORKOUT_LOG", sessaoId);
}

/** Lê os grupos secundários de um exercício (várias grafias da lib/plano) e
 *  devolve sempre um array de strings limpo. */
function normalizeSecondaryGroups(ex) {
  const raw =
    ex?.SECONDARY_MUSCLE_GROUPS ??
    ex?.secondary_muscle_groups ??
    ex?.secondary_muscles ??
    [];
  if (Array.isArray(raw)) return raw.filter((s) => s != null && s !== "");
  if (typeof raw === "string") {
    return raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

// ── Funções exportadas ────────────────────────────────────────────────────────

/**
 * Inicia uma sessão de treino e grava o documento inicial no Firestore.
 * Com persistentLocalCache ativo, o write resolve localmente de imediato.
 * Retorna o sessaoId gerado.
 *
 * @param {{ uid, origemTreino?, diaTreino?, nomeTreino?, exercicios? }} params
 * @returns {Promise<string>} sessaoId
 */
export async function iniciarSessao({
  uid,
  origemTreino,
  diaTreino,
  nomeTreino,
  exercicios = [],
}) {
  if (!uid) throw new Error("[workoutLog] uid obrigatório para iniciarSessao.");

  try {
    const sessaoId = doc(collection(db, "PACIENTE", uid, "WORKOUT_LOG")).id;

    const exerciciosIniciados = exercicios.map((ex) => ({
      EXERCISE_ID: ex.EXERCISE_ID ?? null,
      NOME: ex.NOME ?? ex.nome ?? "",
      MUSCLE_GROUP: ex.MUSCLE_GROUP ?? ex.muscle_group ?? ex.GRUPO_MUSCULAR ?? null,
      // Secundários da exercise_library — persistidos daqui pra frente p/ alimentar
      // o motor de fadiga (muscleFatigue). Treinos antigos não têm → caem no
      // primário/título. Tolerante às duas grafias da lib.
      SECONDARY_MUSCLE_GROUPS: normalizeSecondaryGroups(ex),
      ALVO: {
        series: ex.ALVO?.series ?? null,
        reps: ex.ALVO?.reps ?? null,
        descanso: ex.ALVO?.descanso ?? null,
      },
      SERIES: [],
    }));

    const now = serverTimestamp();

    await setDoc(sessaoRef(uid, sessaoId), {
      SESSAO_ID: sessaoId,
      DATA: dataLocalSP(),
      INICIADO_AT: now,
      FINALIZADO_AT: null,
      ORIGEM_TREINO: origemTreino ?? null,
      DIA_TREINO: diaTreino ?? null,
      NOME_TREINO: nomeTreino ?? null,
      EXERCICIOS: exerciciosIniciados,
      TOTAL_SERIES: 0,
      TONELAGEM_TOTAL: 0,
      STATUS: "EM_ANDAMENTO",
      CREATED_AT: now,
      UPDATED_AT: now,
    });

    return sessaoId;
  } catch (err) {
    console.error("[workoutLog] iniciarSessao:", err);
    throw err;
  }
}

/**
 * Registra uma série concluída em um exercício da sessão ativa.
 * Usa read-merge-write: lê o doc, muta EXERCICIOS em JS, regrava o campo inteiro.
 * Não usa arrayUnion em mapa aninhado (frágil no SDK do Firestore).
 *
 * @param {{ uid, sessaoId, exerciseId, serie: { NUMERO?, CARGA, REPS, RPE?, FEITA_AT? } }} params
 */
export async function registrarSerie({ uid, sessaoId, exerciseId, serie }) {
  if (!uid || !sessaoId || !exerciseId) {
    throw new Error(
      "[workoutLog] uid, sessaoId e exerciseId são obrigatórios para registrarSerie."
    );
  }

  try {
    const ref = sessaoRef(uid, sessaoId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      throw new Error(`[workoutLog] Sessão ${sessaoId} não encontrada.`);
    }

    const exercicios = (snap.data().EXERCICIOS ?? []).map((ex) => {
      if (ex.EXERCISE_ID !== exerciseId) return ex;

      const seriesExistentes = ex.SERIES ?? [];
      const novaSerie = {
        NUMERO: serie.NUMERO ?? seriesExistentes.length + 1,
        CARGA: Number(serie.CARGA) || 0,
        REPS: Number(serie.REPS) || 0,
        RPE: serie.RPE ?? null,
        FEITA_AT: serie.FEITA_AT ?? new Date().toISOString(),
      };

      return { ...ex, SERIES: [...seriesExistentes, novaSerie] };
    });

    await updateDoc(ref, {
      EXERCICIOS: exercicios,
      UPDATED_AT: serverTimestamp(),
    });
  } catch (err) {
    console.error("[workoutLog] registrarSerie:", err);
    throw err;
  }
}

/**
 * Finaliza a sessão, calcula totais e atualiza o documento.
 * Retorna { totalSeries, tonelagemTotal }.
 *
 * @param {{ uid, sessaoId, status?: "CONCLUIDO" | "ABANDONADO" }} params
 * @returns {Promise<{ totalSeries: number, tonelagemTotal: number }>}
 */
export async function finalizarSessao({ uid, sessaoId, status = "CONCLUIDO" }) {
  if (!uid || !sessaoId) {
    throw new Error(
      "[workoutLog] uid e sessaoId são obrigatórios para finalizarSessao."
    );
  }

  try {
    const ref = sessaoRef(uid, sessaoId);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      throw new Error(`[workoutLog] Sessão ${sessaoId} não encontrada.`);
    }

    const { EXERCICIOS = [] } = snap.data();

    let totalSeries = 0;
    let tonelagemTotal = 0;

    for (const ex of EXERCICIOS) {
      for (const s of ex.SERIES ?? []) {
        totalSeries += 1;
        tonelagemTotal += (Number(s.CARGA) || 0) * (Number(s.REPS) || 0);
      }
    }

    await updateDoc(ref, {
      STATUS: status,
      FINALIZADO_AT: serverTimestamp(),
      TOTAL_SERIES: totalSeries,
      TONELAGEM_TOTAL: tonelagemTotal,
      UPDATED_AT: serverTimestamp(),
    });

    return { totalSeries, tonelagemTotal };
  } catch (err) {
    console.error("[workoutLog] finalizarSessao:", err);
    throw err;
  }
}

// ── Sessão FUNCIONAL (Hyrox/CrossFit) ───────────────────────────────────────
// Caminho PARALELO ao clássico: mesmo doc PACIENTE/{uid}/WORKOUT_LOG/{sessaoId},
// mesmos helpers (dataLocalSP/sessaoRef/serverTimestamp), mas com TIPO_LOG:"FUNCIONAL"
// e resultado por BLOCO (não série×carga). NÃO grava TONELAGEM_TOTAL.
// Os leitores clássicos (evolução/PR/fadiga) iteram EXERCICIOS[] → ignoram estas
// sessões naturalmente (não têm EXERCICIOS). Sessão sem TIPO_LOG = clássica.

/**
 * Inicia uma sessão FUNCIONAL e grava o doc inicial. Retorna o sessaoId.
 * @param {{ uid, origemTreino?, diaTreino?, nomeTreino? }} params
 * @returns {Promise<string>} sessaoId
 */
export async function iniciarSessaoFuncional({ uid, origemTreino, diaTreino, nomeTreino }) {
  if (!uid) throw new Error("[workoutLog] uid obrigatório para iniciarSessaoFuncional.");

  try {
    const sessaoId = doc(collection(db, "PACIENTE", uid, "WORKOUT_LOG")).id;
    const now = serverTimestamp();

    await setDoc(sessaoRef(uid, sessaoId), {
      SESSAO_ID: sessaoId,
      TIPO_LOG: "FUNCIONAL",
      DATA: dataLocalSP(),
      INICIADO_AT: now,
      FINALIZADO_AT: null,
      ORIGEM_TREINO: origemTreino ?? null,
      DIA_TREINO: diaTreino ?? null,
      NOME_TREINO: nomeTreino ?? null,
      BLOCOS: [],
      TOTAL_BLOCOS: 0,
      STATUS: "EM_ANDAMENTO",
      CREATED_AT: now,
      UPDATED_AT: now,
    });

    return sessaoId;
  } catch (err) {
    console.error("[workoutLog] iniciarSessaoFuncional:", err);
    throw err;
  }
}

/** Remove chaves nulas/vazias de RESULTADO (não escrevemos nulls). */
function limparResultado(resultado = {}) {
  const out = {};
  for (const [k, v] of Object.entries(resultado)) {
    if (v === null || v === undefined || v === "") continue;
    out[k] = v;
  }
  return out;
}

/**
 * Grava (ou substitui) o RESULTADO de um bloco da sessão funcional.
 * read-merge-write do array BLOCOS (mesmo padrão de registrarSerie).
 * @param {{ uid, sessaoId, blocoId, blockType?, titulo?, resultado, rpe?, notas? }} params
 */
export async function salvarResultadoBloco({ uid, sessaoId, blocoId, blockType, titulo, resultado, rpe, notas }) {
  if (!uid || !sessaoId || !blocoId) {
    throw new Error("[workoutLog] uid, sessaoId e blocoId são obrigatórios para salvarResultadoBloco.");
  }

  try {
    const ref = sessaoRef(uid, sessaoId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error(`[workoutLog] Sessão ${sessaoId} não encontrada.`);

    const blocos = [...(snap.data().BLOCOS ?? [])];

    const entry = {
      BLOCO_ID: blocoId,
      BLOCK_TYPE: blockType ?? null,
      TITULO: titulo ?? null,
      RESULTADO: limparResultado(resultado),
      FEITO_AT: new Date().toISOString(),
    };
    if (rpe != null && rpe !== "") entry.RPE = Number(rpe);
    if (notas) entry.NOTAS = String(notas);

    const idx = blocos.findIndex((b) => b.BLOCO_ID === blocoId);
    if (idx >= 0) blocos[idx] = entry;
    else blocos.push(entry);

    await updateDoc(ref, { BLOCOS: blocos, UPDATED_AT: serverTimestamp() });
  } catch (err) {
    console.error("[workoutLog] salvarResultadoBloco:", err);
    throw err;
  }
}

/**
 * Finaliza a sessão funcional: grava FINALIZADO_AT, TOTAL_BLOCOS e STATUS.
 * @param {{ uid, sessaoId, status?: "CONCLUIDO" | "ABANDONADO" }} params
 * @returns {Promise<{ totalBlocos: number }>}
 */
export async function finalizarSessaoFuncional({ uid, sessaoId, status = "CONCLUIDO" }) {
  if (!uid || !sessaoId) {
    throw new Error("[workoutLog] uid e sessaoId são obrigatórios para finalizarSessaoFuncional.");
  }

  try {
    const ref = sessaoRef(uid, sessaoId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error(`[workoutLog] Sessão ${sessaoId} não encontrada.`);

    const { BLOCOS = [] } = snap.data();

    await updateDoc(ref, {
      STATUS: status,
      FINALIZADO_AT: serverTimestamp(),
      TOTAL_BLOCOS: BLOCOS.length,
      UPDATED_AT: serverTimestamp(),
    });

    return { totalBlocos: BLOCOS.length };
  } catch (err) {
    console.error("[workoutLog] finalizarSessaoFuncional:", err);
    throw err;
  }
}

/**
 * Retorna a evolução de carga de um exercício específico ao longo das sessões.
 * Ignora sessões sem séries com carga > 0 (ex: cardio/tempo).
 *
 * @param {{ uid: string, exerciseId: string }} params
 * @returns {Promise<{ data: string, melhorCarga: number, melhor1RM: number }[]>}
 */
export async function buscarEvolucaoExercicio({ uid, exerciseId, nome, limit }) {
  if (!uid || (!exerciseId && !nome)) return [];
  try {
    // C6 (custo de leitura): mesma janela do fetchWorkoutSessions — o gráfico
    // exibe "últimas N" sessões (N≤50 na UI; "Todas" = todas dentro da janela).
    const snap = await getDocs(
      query(
        collection(db, "PACIENTE", uid, "WORKOUT_LOG"),
        orderBy("CREATED_AT", "desc"),
        fsLimit(WORKOUT_SESSIONS_WINDOW),
      )
    );

    const nomeNorm = normNome(nome);
    const pontos = [];
    snap.forEach((docSnap) => {
      const d = docSnap.data();
      // Match por EXERCISE_ID; fallback por NOME normalizado.
      const ex = (d.EXERCICIOS ?? []).find((e) => exMatches(e, { exerciseId, nomeNorm }));
      if (!ex) return;

      const seriesValidas = (ex.SERIES ?? []).filter(
        (s) => Number(s.CARGA) > 0 && Number(s.REPS) > 0
      );
      if (seriesValidas.length === 0) return;

      let melhorCarga = 0;
      let melhor1RM = 0;
      for (const s of seriesValidas) {
        const carga = Number(s.CARGA);
        const reps = Number(s.REPS);
        const rm = epley1RM(carga, reps); // Epley reusado (utils/epley.js)
        if (carga > melhorCarga) melhorCarga = carga;
        if (rm > melhor1RM) melhor1RM = rm;
      }

      pontos.push({ data: d.DATA ?? "", melhorCarga, melhor1RM });
    });

    pontos.sort((a, b) => (a.data > b.data ? 1 : a.data < b.data ? -1 : 0));
    // "Últimas N sessões" (mantém as mais recentes).
    return Number(limit) > 0 ? pontos.slice(-Number(limit)) : pontos;
  } catch (err) {
    console.error("[workoutLog] buscarEvolucaoExercicio:", err);
    return [];
  }
}
